import React, { useState, useEffect, useRef } from 'react';
import './AdminOrders.css';
import { useAuth } from '../../../contexts/AuthContext';
import { db, functions } from '../../../firebase';
import { collection, query, orderBy, onSnapshot, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const STATUS_OPTIONS = ['pending', 'roasting', 'delivered', 'cancelled'];

const STATUS_LABELS = {
    pending: 'Pending',
    roasting: 'Brewing',
    shipped: 'Shipped',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
};

function AdminOrders() {
    const { currentUser, isAdmin, loading: authLoading, login, logout } = useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    const [expandedOrder, setExpandedOrder] = useState(null);
    const [soundOn, setSoundOn] = useState(
        () => localStorage.getItem('otr_order_sound') !== 'off'
    );

    const initializedRef = useRef(false);
    const soundOnRef = useRef(soundOn);
    soundOnRef.current = soundOn;
    const voiceRef = useRef(null);

    const isAuthorized = currentUser && isAdmin && currentUser.email === 'info@offftheroast.com';

    // Pick the most natural-sounding English voice the device offers.
    const pickVoice = () => {
        const synth = window.speechSynthesis;
        if (!synth) return;
        const voices = synth.getVoices();
        if (!voices || voices.length === 0) return;
        const prefer = [
            'Samantha', 'Google US English', 'Microsoft Aria', 'Microsoft Jenny',
            'Ava', 'Allison', 'Serena', 'Karen', 'Moira', 'Tessa',
        ];
        let chosen = null;
        for (const name of prefer) {
            chosen = voices.find(v => v.name && v.name.includes(name));
            if (chosen) break;
        }
        if (!chosen) chosen = voices.find(v => v.lang && v.lang.startsWith('en-US'));
        if (!chosen) chosen = voices.find(v => v.lang && v.lang.startsWith('en'));
        voiceRef.current = chosen || voices[0];
    };

    // Speak the alert in a warm, natural human voice (built-in, no audio file).
    const announce = () => {
        const synth = window.speechSynthesis;
        if (!synth) return;
        if (!voiceRef.current) pickVoice();
        synth.cancel();
        const u = new SpeechSynthesisUtterance('You have a new order');
        if (voiceRef.current) u.voice = voiceRef.current;
        u.rate = 0.95;
        u.pitch = 1.05;
        u.volume = 1;
        synth.speak(u);
    };

    // Load voices (they arrive asynchronously) and prime speech on first interaction.
    useEffect(() => {
        if (!window.speechSynthesis) return;
        pickVoice();
        window.speechSynthesis.onvoiceschanged = pickVoice;
        const prime = () => pickVoice();
        window.addEventListener('click', prime, { once: true });
        window.addEventListener('keydown', prime, { once: true });
        return () => {
            window.removeEventListener('click', prime);
            window.removeEventListener('keydown', prime);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleSound = () => {
        setSoundOn((prev) => {
            const next = !prev;
            localStorage.setItem('otr_order_sound', next ? 'on' : 'off');
            if (next) announce();
            return next;
        });
    };

    useEffect(() => {
        if (!isAuthorized) {
            setLoading(false);
            return;
        }

        initializedRef.current = false;
        const q = query(collection(db, 'orders'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            // Chime for orders that arrive AFTER the first load (skip the initial batch)
            if (initializedRef.current) {
                const newOnes = snapshot
                    .docChanges()
                    .filter(c => c.type === 'added' && !c.doc.metadata.hasPendingWrites);
                if (newOnes.length > 0 && soundOnRef.current) {
                    announce();
                }
            }
            setOrders(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
            initializedRef.current = true;
        }, () => {
            setLoading(false);
        });
        return () => unsubscribe();
    }, [isAuthorized]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        setLoginLoading(true);
        try {
            await login(email, password);
        } catch {
            setLoginError('Invalid email or password.');
        }
        setLoginLoading(false);
    };

    const updateStatus = async (orderId, newStatus) => {
        await updateDoc(doc(db, 'orders', orderId), { status: newStatus });
    };

    const [cancellingId, setCancellingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const deleteOrder = async (e, order) => {
        e.stopPropagation();
        const warn = (order.stripePaymentIntent || order.stripeSessionId) && order.refunded !== true
            ? `\n\nNOTE: this order was paid online and has NOT been refunded. Deleting it will NOT refund the customer — refund first if needed.`
            : '';
        if (!window.confirm(
            `Permanently DELETE ${order.customerName}'s transaction ($${(order.total || 0).toFixed(2)})?\n\nThis cannot be undone and removes it from your records and revenue.${warn}`
        )) return;
        setDeletingId(order.id);
        try {
            await deleteDoc(doc(db, 'orders', order.id));
        } catch (err) {
            console.error(err);
            alert('Could not delete this transaction: ' + (err.message || 'Please try again.'));
        }
        setDeletingId(null);
    };

    const quickCancel = async (e, order) => {
        e.stopPropagation(); // don't expand the card
        const paidOnline = !order.refunded && (order.stripePaymentIntent || order.stripeSessionId);
        const msg = paidOnline
            ? `Cancel ${order.customerName}'s order and REFUND their card in full? This cannot be undone.`
            : `Cancel ${order.customerName}'s order? This marks it Cancelled.`;
        if (!window.confirm(msg)) return;
        setCancellingId(order.id);
        try {
            if (paidOnline) {
                const refundOrder = httpsCallable(functions, 'refundOrder');
                const res = await refundOrder({ orderId: order.id });
                if (res.data && res.data.refunded) {
                    alert(`Order cancelled and card refunded in full.`);
                } else if (res.data && res.data.alreadyRefunded) {
                    alert(`Order cancelled. It was already refunded.`);
                } else {
                    alert(`Order cancelled. (No online payment was on file to refund.)`);
                }
            } else {
                await updateStatus(order.id, 'cancelled');
            }
        } catch (err) {
            console.error(err);
            alert('Could not cancel/refund this order: ' + (err.message || 'Please try again.'));
        }
        setCancellingId(null);
    };

    const paymentLabel = (order) => {
        if (order.paymentMethod === 'in-person') return '💵 In person (cash/card)';
        if (order.paymentMethod === 'online' || order.stripeSessionId) return '💳 Paid online';
        return '—';
    };

    const formatDate = (ts) => {
        if (!ts) return '—';
        const date = ts.toDate ? ts.toDate() : new Date(ts);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
            ' · ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const filteredOrders = filter === 'all'
        ? orders
        : orders.filter(o => o.status === filter);

    const countFor = (status) => orders.filter(o => o.status === status).length;

    // Wait for auth to resolve before showing the login form
    if (authLoading) return null;

    // Not logged in
    if (!currentUser) {
        return (
            <div className='ao-container'>
                <h1 className='ao-title'>ADMIN</h1>
                <form className='ao-login' onSubmit={handleLogin}>
                    <h2>Sign In</h2>
                    {loginError && <div className='ao-login__error'>{loginError}</div>}
                    <div className='ao-login__group'>
                        <label>Email</label>
                        <input type='email' value={email} onChange={e => setEmail(e.target.value)} required />
                    </div>
                    <div className='ao-login__group'>
                        <label>Password</label>
                        <input type='password' value={password} onChange={e => setPassword(e.target.value)} required />
                    </div>
                    <button type='submit' className='ao-login__btn' disabled={loginLoading}>
                        {loginLoading ? 'SIGNING IN...' : 'SIGN IN'}
                    </button>
                </form>
            </div>
        );
    }

    // Not authorized
    if (!isAuthorized) {
        return (
            <div className='ao-container'>
                <h1 className='ao-title'>ADMIN</h1>
                <div className='ao-denied'>
                    <p>Access denied. You do not have admin privileges.</p>
                    <button className='ao-denied__btn' onClick={logout}>SIGN OUT</button>
                </div>
            </div>
        );
    }

    return (
        <div className='ao-container'>
            <div className='ao-header'>
                <h1 className='ao-title'>DRINK ORDERS TRACKER</h1>
                <div className='ao-header__actions'>
                    <button
                        className={`ao-sound${soundOn ? ' ao-sound--on' : ''}`}
                        onClick={toggleSound}
                        title={soundOn ? 'Sound alerts ON — click to mute' : 'Sound alerts OFF — click to enable'}
                    >
                        {soundOn ? '🔔 Sound On' : '🔕 Sound Off'}
                    </button>
                    <button className='ao-sound' onClick={announce} title='Play the voice alert'>
                        Test
                    </button>
                    <button className='ao-logout' onClick={logout}>SIGN OUT</button>
                </div>
            </div>

            {/* Summary bar */}
            <div className='ao-summary'>
                <div className='ao-summary__stat'>
                    <span className='ao-summary__num'>{orders.length}</span>
                    <span className='ao-summary__label'>Total Orders</span>
                </div>
                <div className='ao-summary__stat'>
                    <span className='ao-summary__num ao-summary__num--pending'>{countFor('pending')}</span>
                    <span className='ao-summary__label'>Pending</span>
                </div>
                <div className='ao-summary__stat'>
                    <span className='ao-summary__num ao-summary__num--roasting'>{countFor('roasting')}</span>
                    <span className='ao-summary__label'>Brewing</span>
                </div>
                <div className='ao-summary__stat'>
                    <span className='ao-summary__num ao-summary__num--delivered'>{countFor('delivered')}</span>
                    <span className='ao-summary__label'>Delivered</span>
                </div>
                <div className='ao-summary__stat'>
                    <span className='ao-summary__num ao-summary__num--revenue'>
                        ${orders
                            .filter(o => o.status === 'delivered' && o.refunded !== true)
                            .reduce((sum, o) => sum + (o.total || 0), 0)
                            .toFixed(2)}
                    </span>
                    <span className='ao-summary__label'>Total Revenue</span>
                </div>
            </div>

            {/* Filter tabs */}
            <div className='ao-filters'>
                {['all', ...STATUS_OPTIONS].map(s => (
                    <button
                        key={s}
                        className={`ao-filter-btn${filter === s ? ' active' : ''}`}
                        onClick={() => setFilter(s)}
                    >
                        {s === 'all' ? `All (${orders.length})` : `${STATUS_LABELS[s]} (${countFor(s)})`}
                    </button>
                ))}
            </div>

            {loading ? (
                <p className='ao-empty'>Loading orders...</p>
            ) : filteredOrders.length === 0 ? (
                <p className='ao-empty'>No orders found.</p>
            ) : (
                <div className='ao-list'>
                    {filteredOrders.map(order => (
                        <div key={order.id} className={`ao-card ao-card--${order.status}`}>
                            <div
                                className='ao-card__header'
                                onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                            >
                                <div className='ao-card__left'>
                                    <span className={`ao-status-badge ao-status-badge--${order.status}`}>
                                        {STATUS_LABELS[order.status] || order.status}
                                    </span>
                                    <div className='ao-card__customer'>
                                        <span className='ao-card__name'>{order.customerName}</span>
                                        <span className='ao-card__email'>{order.customerEmail}</span>
                                    </div>
                                </div>
                                <div className='ao-card__right'>
                                    <span className='ao-card__total'>${(order.total || 0).toFixed(2)}</span>
                                    <span className='ao-card__date'>{formatDate(order.createdAt)}</span>
                                    <span className='ao-card__delivery'>
                                        {order.deliveryType === 'pickup' ? '🏪 Pickup' : '📦 Ship'}
                                    </span>
                                    <span className='ao-card__payment'>
                                        {paymentLabel(order)}
                                    </span>
                                    {order.refunded && (
                                        <span className='ao-refunded-tag'>Refunded</span>
                                    )}
                                    {order.status !== 'cancelled' && order.status !== 'delivered' && (
                                        <button
                                            className='ao-quick-cancel'
                                            onClick={(e) => quickCancel(e, order)}
                                            disabled={cancellingId === order.id}
                                            title={(order.stripePaymentIntent || order.stripeSessionId)
                                                ? 'Cancel this order and refund the card'
                                                : 'Cancel this order'}
                                        >
                                            {cancellingId === order.id ? 'Processing…' : '✕ Cancel'}
                                        </button>
                                    )}
                                    <span className='ao-card__chevron'>{expandedOrder === order.id ? '▲' : '▼'}</span>
                                </div>
                            </div>

                            {expandedOrder === order.id && (
                                <div className='ao-card__body'>
                                    {/* Items */}
                                    <div className='ao-card__items'>
                                        <p className='ao-card__section-label'>ORDER ITEMS</p>
                                        {(order.items || []).map((item, i) => (
                                            <div key={i} className='ao-item-row'>
                                                <span className='ao-item-row__name'>{item.name}</span>
                                                <span className='ao-item-row__qty'>× {item.quantity}</span>
                                                <span className='ao-item-row__price'>${(item.total || 0).toFixed(2)}</span>
                                            </div>
                                        ))}
                                        <div className='ao-item-row ao-item-row--total'>
                                            <span>Shipping</span>
                                            <span></span>
                                            <span>${(order.shippingCost || 0).toFixed(2)}</span>
                                        </div>
                                        <div className='ao-item-row ao-item-row--total ao-item-row--grand'>
                                            <span>Total</span>
                                            <span></span>
                                            <span>${(order.total || 0).toFixed(2)}</span>
                                        </div>
                                    </div>

                                    {/* Shipping address */}
                                    {order.shippingAddress && order.deliveryType !== 'pickup' && (
                                        <div className='ao-card__address'>
                                            <p className='ao-card__section-label'>SHIP TO</p>
                                            <p>{order.shippingAddress.name}</p>
                                            <p>{order.shippingAddress.line1}{order.shippingAddress.line2 ? `, ${order.shippingAddress.line2}` : ''}</p>
                                            <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}</p>
                                            <p>{order.shippingAddress.country}</p>
                                        </div>
                                    )}
                                    {order.deliveryType === 'pickup' && (
                                        <div className='ao-card__address'>
                                            <p className='ao-card__section-label'>PICKUP LOCATION</p>
                                            {order.orderType === 'cafe' ? (
                                                <p>At the sign</p>
                                            ) : (
                                                <>
                                                    <p>Epicure Club Cafe and Wine Bar</p>
                                                    <p>820 Willow St #100, San Jose, CA 95125</p>
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {/* Status update */}
                                    <div className='ao-card__status-update'>
                                        <p className='ao-card__section-label'>UPDATE STATUS</p>
                                        <div className='ao-status-btns'>
                                            {STATUS_OPTIONS.map(s => (
                                                <button
                                                    key={s}
                                                    className={`ao-status-btn ao-status-btn--${s}${order.status === s ? ' active' : ''}`}
                                                    onClick={() => updateStatus(order.id, s)}
                                                >
                                                    {STATUS_LABELS[s]}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Stripe ID */}
                                    <p className='ao-card__stripe-id'>Stripe session: {order.stripeSessionId}</p>

                                    {/* Danger zone */}
                                    <div className='ao-card__danger'>
                                        <span className='ao-card__danger-label'>
                                            Permanently remove this transaction from your records
                                        </span>
                                        <button
                                            className='ao-delete-btn'
                                            onClick={(e) => deleteOrder(e, order)}
                                            disabled={deletingId === order.id}
                                        >
                                            {deletingId === order.id ? 'Deleting…' : '🗑 Delete transaction'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default AdminOrders;
